import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { ChevronLeft, Play, Square, RotateCcw, Check, Lock, GripVertical, Sparkles } from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { EXERCISES, PILLARS, DURATIONS, PROGRESSION, blissTarget, timerSecondsFor, isHoldEx } from '../../data/powerExercises';
import { tick, lionRoar, falconPhoenix, speak, stopSpeech } from '../../utils/powerSounds';
import { pingAuraFuse } from '../../utils/auraPing';

/* ═══════════════════════════════════════════════════════════════════
   THE POWER — Bliss Imperial Challenge Engine
   Screens: setup → calendar → day → timer
   Aesthetic: deep obsidian + gold-line SVG silhouettes.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const GOLD_BRIGHT = '#E9C77A';
const BG = '#050308';
const EX_BY_ID = Object.fromEntries(EXERCISES.map(e => [e.id, e]));

/* Pillar-specific CSS filter — shifts the gold source images to each
   Animal Pillar's colour while keeping clarity and the neon glow. */
const PILLAR_FILTER = {
  Lion:     'none',
  Stallion: 'hue-rotate(-10deg) brightness(0.98) saturate(1.1)',
  Eagle:    'hue-rotate(12deg) brightness(1.12) saturate(0.95)',
  Leopard:  'hue-rotate(-22deg) brightness(0.88) saturate(1.25)',
};
const pillarFilter = (pillarKey) => PILLAR_FILTER[pillarKey] || 'none';

/* ────────────── SVG SILHOUETTE ──────────────
   Renders a gold-line figure from a joint pose. If given an array of
   poses + a continuous t (0..poses.length), lerps smoothly between
   adjacent keyframes — creating a looping "video" out of pure SVG. */
const lerp = (a, b, k) => a + (b - a) * k;
const easeInOutSine = (k) => -(Math.cos(Math.PI * k) - 1) / 2;
const lerpPose = (a, b, k) => {
  const out = {};
  const kk = easeInOutSine(k);
  for (const key of Object.keys(a)) {
    out[key] = [lerp(a[key][0], b[key][0], kk), lerp(a[key][1], b[key][1], kk)];
  }
  return out;
};

const Silhouette = ({ pose, poses, t = 0, size = 180, stroke = GOLD, glow = true }) => {
  let P = pose;
  if (!P && poses && poses.length > 0) {
    const n = poses.length;
    const tt = ((t % n) + n) % n;
    const i = Math.floor(tt);
    const j = (i + 1) % n;
    P = lerpPose(poses[i], poses[j], tt - i);
  }
  if (!P) return null;
  const line = (a, b) => (
    <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={stroke} strokeWidth="1.6"
          strokeLinecap="round"/>
  );
  return (
    <svg viewBox="0 0 100 60" width={size} height={size * 0.6}
      style={{ filter: glow ? `drop-shadow(0 0 6px ${stroke}80)` : 'none' }}>
      {/* head */}
      <circle cx={P.head[0]} cy={P.head[1]} r="3.8"
        stroke={stroke} strokeWidth="1.6" fill="none"/>
      {/* torso spine */}
      {line(P.neck, P.hip)}
      {/* arms */}
      {line(P.neck, P.shL)} {line(P.shL, P.elL)} {line(P.elL, P.hnL)}
      {line(P.neck, P.shR)} {line(P.shR, P.elR)} {line(P.elR, P.hnR)}
      {/* legs */}
      {line(P.hip, P.knL)} {line(P.knL, P.ftL)}
      {line(P.hip, P.knR)} {line(P.knR, P.ftR)}
    </svg>
  );
};

/* ────────────── HELPERS ────────────── */
const currentWeek = (dayIndex) => Math.floor(dayIndex / 7) + 1;
const progressionFor = (dayIndex) => {
  const w = currentWeek(dayIndex);
  if (w <= 2) return PROGRESSION[0];
  if (w <= 4) return PROGRESSION[1];
  return PROGRESSION[2];
};

/* ────────────── SETUP ────────────── */
const Pill = ({ label, active, onClick, testid, w = 'auto' }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    style={{
      background: active ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})` : 'rgba(255,255,255,0.03)',
      color: active ? '#0a0a0a' : 'rgba(255,255,255,0.7)',
      border: active ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
      boxShadow: active ? `0 0 14px rgba(197,160,89,0.35)` : 'none',
      width: w,
    }}
    className="rounded-lg py-2 px-3 text-[11px] font-['Cinzel'] uppercase tracking-[0.2em] transition-all">
    {label}
  </button>
);

const Setup = ({ initial, onSaved }) => {
  const [gender, setGender] = useState(initial?.gender || 'male');
  const [intensity, setIntensity] = useState(initial?.intensity || 'normal');
  const [durationKey, setDurationKey] = useState(initial?.duration_key || 'w3');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/power/profile`, {
        gender, intensity, duration_key: durationKey,
      });
      onSaved(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create the challenge.');
    } finally { setSaving(false); }
  };

  const dbyTier = useMemo(() => {
    const g = {};
    DURATIONS.forEach(d => { (g[d.tier] = g[d.tier] || []).push(d); });
    return g;
  }, []);

  return (
    <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="power-setup">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <p className="font-['Outfit'] uppercase tracking-[0.35em] mb-1" style={{ color: GOLD, fontSize: 10 }}>
            Initialization
          </p>
          <h2 className="font-['Cinzel'] tracking-[0.14em]"
            style={{ color: '#FFE9A0', fontSize: 22, textShadow: '0 0 14px rgba(197,160,89,0.35)' }}>
            The Imperial Challenge
          </h2>
          <p className="mt-3 font-['Outfit'] text-[11px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.55)' }}>
            Declare your vessel. The Engine shall forge a calibrated protocol
            of movement — from Form Calibration to Imperial Mastery.
          </p>
        </div>

        <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
          style={{ color: GOLD, fontSize: 10 }}>Identity</p>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Pill label="Male"   active={gender==='male'}   onClick={()=>setGender('male')}   testid="power-gender-male"/>
          <Pill label="Female" active={gender==='female'} onClick={()=>setGender('female')} testid="power-gender-female"/>
        </div>

        <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
          style={{ color: GOLD, fontSize: 10 }}>Intensity</p>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <Pill label="Normal"   active={intensity==='normal'}   onClick={()=>setIntensity('normal')}   testid="power-intensity-normal"/>
          <Pill label="Active"   active={intensity==='active'}   onClick={()=>setIntensity('active')}   testid="power-intensity-active"/>
          <Pill label="Athletic" active={intensity==='athletic'} onClick={()=>setIntensity('athletic')} testid="power-intensity-athletic"/>
        </div>

        <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
          style={{ color: GOLD, fontSize: 10 }}>Duration · Imperial Library</p>
        {Object.entries(dbyTier).map(([tier, arr]) => (
          <div key={tier} className="mb-3">
            <p className="font-['Outfit'] uppercase tracking-[0.22em] mb-1.5"
              style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9 }}>{tier}</p>
            <div className="flex flex-wrap gap-1.5">
              {arr.map(d => (
                <Pill key={d.key} label={d.label}
                  active={durationKey===d.key}
                  onClick={()=>setDurationKey(d.key)}
                  testid={`power-duration-${d.key}`}/>
              ))}
            </div>
          </div>
        ))}

        {err && (
          <p className="mb-3 text-[11px] font-['Outfit'] text-center" style={{ color: '#e88a7a' }}>{err}</p>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="power-forge-btn"
          className="w-full rounded-xl py-3 mt-3 font-['Cinzel'] uppercase tracking-[0.3em] transition-all"
          style={{
            background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            color: '#0a0a0a', fontSize: 12,
            boxShadow: '0 0 20px rgba(197,160,89,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
            opacity: saving ? 0.6 : 1,
          }}>
          {saving ? 'Forging Protocol…' : 'Forge the Protocol →'}
        </button>

        <p className="mt-6 text-[9px] leading-relaxed font-['Outfit'] text-center"
          style={{ color: 'rgba(255,255,255,0.35)' }}>
          Disclaimer: The Bliss Physical Protocol is for tracking and educational purposes only.
          Movements involve physical exertion. Always prioritize correct form over intensity. If
          you experience sharp pain or discomfort, cease the activity immediately. Consult with a
          qualified healthcare professional before beginning any new regimen.
        </p>
      </div>
    </div>
  );
};

/* ────────────── CALENDAR (day grid) ────────────── */
const USER_CAL_KEY = 'bliss_power_user_calendar_v1';
const readUserCal = () => {
  try { return JSON.parse(localStorage.getItem(USER_CAL_KEY) || '{}'); } catch { return {}; }
};
const writeUserCal = (obj) => {
  try { localStorage.setItem(USER_CAL_KEY, JSON.stringify(obj)); } catch { /* noop */ }
};

/* ────────────── USER CALENDAR (matches Bliss plan duration, localStorage) ────────────── */
const UserCalendar = ({ customs, onOpenUserDay, totalDays }) => {
  const [cal, setCal] = useState(readUserCal());
  const days = Array.from({ length: totalDays }, (_, i) => i);
  const nextIdx = days.find((i) => !cal[i]?.done);
  const activeIdx = nextIdx === undefined ? totalDays - 1 : nextIdx;
  const completed = days.filter((i) => cal[i]?.done).length;
  // Refresh on window focus so it reflects storage writes from Timer
  useEffect(() => {
    const onFocus = () => setCal(readUserCal());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  return (
    <div className="mt-3 rounded-xl px-3 py-3"
      style={{ background: 'rgba(255,158,184,0.04)', border: '1px solid rgba(255,158,184,0.25)' }}
      data-testid="power-user-calendar">
      <div className="flex items-baseline justify-between mb-2">
        <p className="font-['Outfit'] uppercase tracking-[0.28em]"
          style={{ color: '#FF9EB8', fontSize: 9 }}>User Calendar · {totalDays} Days</p>
        <p className="font-['Outfit'] uppercase tracking-[0.2em]"
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9 }}>
          {completed} / {totalDays}
        </p>
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="power-user-day-grid">
        {days.map((i) => {
          const done = !!cal[i]?.done;
          const assigned = (cal[i]?.exercises || []).length;
          const isActive = i === activeIdx;
          const bg = done
            ? `linear-gradient(180deg, #FF9EB8, #a25a70)`
            : isActive ? `linear-gradient(180deg, rgba(255,158,184,0.25), rgba(255,158,184,0.05))`
            : 'rgba(255,255,255,0.03)';
          const border = done
            ? `1px solid #FF9EB8`
            : isActive ? `1px solid #FF9EB8`
            : '1px solid rgba(255,255,255,0.08)';
          return (
            <button key={i}
              onClick={() => onOpenUserDay?.(i)}
              data-testid={`power-user-day-${i}`}
              className="aspect-square rounded-lg flex flex-col items-center justify-center transition-all"
              style={{
                background: bg, border,
                boxShadow: isActive ? `0 0 10px rgba(255,158,184,0.4)` : 'none',
              }}>
              {done ? <Check size={12} color="#0a0a0a" strokeWidth={3}/> : null}
              <span className="font-['Cinzel']"
                style={{ color: done ? '#0a0a0a' : isActive ? '#FF9EB8' : 'rgba(255,255,255,0.55)', fontSize: 10 }}>
                {i + 1}
              </span>
              {assigned > 0 && !done && (
                <span className="font-['Outfit']" style={{ color: '#FF9EB8', fontSize: 7 }}>
                  {assigned}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-2 font-['Cormorant_Garamond'] italic text-center"
        style={{ color: 'rgba(255,158,184,0.7)', fontSize: 10 }}>
        Tap a day to assign custom exercises · Same length as Bliss protocol
      </p>
    </div>
  );
};

const Calendar = ({ plan, customs, onOpenDay, onOpenLibrary, onReset, onOpenCustom, onDeleteCustom, onOpenUserDay, tab, setTab }) => {
  const nextIdx = plan.days.findIndex((d) => !d.done);
  const activeIdx = nextIdx === -1 ? plan.days.length - 1 : nextIdx;
  const completed = plan.days.filter(d => d.done).length;

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4"
      style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 40px)' }}
      data-testid="power-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: GOLD, fontSize: 9 }}>
            Active Protocol
          </p>
          <p className="font-['Cinzel'] tracking-[0.12em]"
            style={{ color: '#FFE9A0', fontSize: 16 }}>
            {DURATIONS.find(d => d.key === plan.duration_key)?.label || plan.duration_key}
            {' · '}
            {plan.intensity[0].toUpperCase() + plan.intensity.slice(1)}
          </p>
        </div>
        <button onClick={onReset}
          data-testid="power-reset-btn"
          className="text-[9px] font-['Outfit'] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full"
          style={{ color: GOLD, border: `1px solid ${GOLD}40` }}>
          Reset
        </button>
      </div>

      {/* Sub-tabs — Bliss Protocol (default) · User Protocol (custom) · Library (all) */}
      <div className="mb-4 grid grid-cols-3 gap-1.5 rounded-full p-1"
        style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33` }}
        data-testid="power-subtabs">
        {[
          { id: 'bliss',   label: 'Bliss'   },
          { id: 'user',    label: 'User'    },
          { id: 'library', label: 'Library' },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className="py-1.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.18em] transition-all"
              style={{
                background: active ? `linear-gradient(135deg, #FFE9A0, ${GOLD_SOFT})` : 'transparent',
                color: active ? '#050308' : '#FFE9A0',
                boxShadow: active ? `0 0 10px ${GOLD}77` : 'none',
              }}
              data-testid={`power-subtab-${t.id}`}>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'bliss' && (
      <>

      {/* Progress */}
      <div className="rounded-2xl px-4 py-3 mb-4 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(20,15,10,0.9), rgba(5,3,8,0.95))',
          border: `1px solid ${GOLD}55`,
          boxShadow: '0 0 20px rgba(197,160,89,0.15)',
        }}>
        <div className="flex items-baseline justify-between mb-1.5">
          <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: GOLD, fontSize: 10 }}>
            Progress
          </p>
          <p className="font-['Outfit'] uppercase tracking-[0.2em]"
            style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
            {completed} / {plan.total_days} days
          </p>
        </div>
        <div className="w-full h-1.5 rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(completed / plan.total_days) * 100}%`, background: GOLD, boxShadow: `0 0 8px ${GOLD}` }}/>
        </div>
        <p className="mt-2 font-['Outfit']" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>
          {progressionFor(activeIdx).weeks} · <span style={{ color: '#FFE9A0' }}>{progressionFor(activeIdx).focus}</span>
        </p>
      </div>

      {/* Day grid */}
      <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-2"
        style={{ color: GOLD, fontSize: 9 }}>Calendar</p>
      <div className="grid grid-cols-7 gap-1.5" data-testid="power-day-grid">
        {plan.days.map((d, i) => {
          const locked = !d.done && i > activeIdx;
          const isActive = i === activeIdx;
          const bg = d.done
            ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`
            : d.rest
              ? 'rgba(255,255,255,0.02)'
              : isActive
                ? `linear-gradient(180deg, rgba(197,160,89,0.25), rgba(197,160,89,0.05))`
                : 'rgba(255,255,255,0.03)';
          const border = d.done
            ? `1px solid ${GOLD}`
            : isActive && !d.rest
              ? `1px solid ${GOLD}`
              : '1px solid rgba(255,255,255,0.08)';
          return (
            <button key={i}
              onClick={() => { if (!locked) onOpenDay(i); }}
              disabled={locked}
              data-testid={`power-day-${i}`}
              className="aspect-square rounded-lg flex flex-col items-center justify-center transition-all"
              style={{
                background: bg, border,
                boxShadow: isActive && !d.rest ? `0 0 12px ${GOLD}55` : 'none',
                opacity: locked ? 0.5 : 1,
                cursor: locked ? 'not-allowed' : 'pointer',
              }}>
              {d.done ? (
                <Check size={12} color="#0a0a0a" strokeWidth={3}/>
              ) : d.rest ? (
                <span className="font-['Outfit'] uppercase tracking-[0.1em]" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 8 }}>Rest</span>
              ) : locked ? (
                <Lock size={10} color="rgba(255,255,255,0.5)"/>
              ) : null}
              <span className="font-['Cinzel']"
                style={{ color: d.done ? '#0a0a0a' : isActive ? '#FFE9A0' : 'rgba(255,255,255,0.55)', fontSize: 10, marginTop: d.rest || d.done || locked ? 1 : 0 }}>
                {i + 1}
              </span>
            </button>
          );
        })}
      </div>

      {/* Progression legend */}
      <div className="mt-5 rounded-xl px-3 py-2.5"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-2"
          style={{ color: GOLD, fontSize: 9 }}>Progression · Weekly Focus</p>
        {PROGRESSION.map((p) => (
          <div key={p.weeks} className="flex items-baseline gap-2 py-0.5">
            <span className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 10, minWidth: 70 }}>{p.weeks}</span>
            <span className="font-['Outfit']" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>{p.focus}</span>
          </div>
        ))}
      </div>

      </>
      )}

      {tab === 'user' && (
      <>

      {/* Custom exercises library — User Protocol tab */}
      <div className="mt-1 rounded-xl px-3 py-3"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between mb-2">
          <p className="font-['Outfit'] uppercase tracking-[0.28em]"
            style={{ color: GOLD, fontSize: 9 }}>My Custom Exercises</p>
          <button onClick={onOpenCustom}
            data-testid="power-calendar-add-custom-btn"
            className="rounded-full px-2.5 py-1 flex items-center gap-1"
            style={{ background: 'rgba(197,160,89,0.1)', border: `1px solid ${GOLD}55`, color: GOLD, fontSize: 10 }}>
            <span className="font-['Cinzel'] uppercase tracking-[0.2em]">+ Forge</span>
          </button>
        </div>
        {customs && customs.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="power-custom-library">
            {customs.map((c) => {
              const pcol = PILLARS[c.pillar]?.color || GOLD;
              let holdTimer = null;
              const startHold = () => {
                holdTimer = setTimeout(() => {
                  if (window.confirm(`Delete "${c.name}"? It will be removed from all assigned days.`)) {
                    onDeleteCustom(c.id);
                  }
                }, 650);
              };
              const cancelHold = () => { if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; } };
              return (
                <div key={c.id}
                  onPointerDown={startHold}
                  onPointerUp={cancelHold}
                  onPointerLeave={cancelHold}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid={`power-custom-card-${c.id}`}
                  className="flex-shrink-0 rounded-lg px-2 py-2 flex flex-col items-center relative select-none"
                  style={{
                    background: '#050308', border: `1px solid ${pcol}66`,
                    boxShadow: `0 0 10px ${pcol}22`, width: 84,
                    touchAction: 'manipulation',
                  }}>
                  {c.images?.[0] ? (
                    <img src={`${BACKEND_URL}${c.images[0]}`} alt=""
                      style={{ width: 64, height: 44, objectFit: 'contain',
                        filter: `${pillarFilter(c.pillar)} drop-shadow(0 0 4px ${pcol}66)` }}/>
                  ) : (
                    <div style={{ width: 64, height: 44 }}/>
                  )}
                  <p className="mt-1 text-center font-['Cinzel'] truncate w-full"
                    style={{ color: '#FFE9A0', fontSize: 9 }}>{c.name}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center font-['Outfit'] py-2"
            style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
            Forge your own exercises with one image each. Tap “+ Forge” to begin.
          </p>
        )}
      </div>
      <p className="mt-2 font-['Cormorant_Garamond'] italic text-center"
        style={{ color: `${GOLD}88`, fontSize: 10 }}>
        Long-press any custom card to remove
      </p>

      {/* User Protocol Calendar — 14-day grid (localStorage backed) */}
      <UserCalendar customs={customs} onOpenUserDay={onOpenUserDay} totalDays={plan.days.length}/>

      </>
      )}

      {tab === 'library' && (
      <>
      {/* Library — all Bliss defaults + user forged exercises as tile grid (like Force's All grid) */}
      <div className="rounded-xl px-2 py-3"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="power-library-tab">
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: GOLD, fontSize: 9 }}>
            All Exercises · {EXERCISES.length + (customs?.length || 0)}
          </p>
          <p className="font-['Cormorant_Garamond'] italic" style={{ color: `${GOLD}88`, fontSize: 10 }}>
            Long-press user tiles to remove
          </p>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {/* Bliss defaults (gold badge) */}
          {EXERCISES.map((e) => {
            const pcol = PILLARS[e.pillar]?.color || GOLD;
            return (
              <button key={`bliss-${e.id}`} type="button" onClick={() => onOpenLibrary?.(e.id)}
                data-testid={`power-lib-bliss-${e.id}`}
                className="relative flex flex-col items-center rounded-xl overflow-hidden active:scale-95 transition-transform"
                style={{
                  aspectRatio: '1 / 1.15',
                  background: `linear-gradient(160deg, rgba(197,160,89,0.14), rgba(0,0,0,0.6))`,
                  border: `1px solid ${pcol}55`,
                  boxShadow: `0 0 8px ${pcol}22`,
                }}>
                {e.images?.[0] ? (
                  <img src={`${BACKEND_URL}${e.images[0]}`} alt={e.name}
                    style={{ width: '82%', height: '58%', objectFit: 'contain', marginTop: 4,
                      filter: `${pillarFilter(e.pillar)} drop-shadow(0 0 5px ${pcol}66)` }}/>
                ) : (
                  <div style={{ width: '82%', height: '58%',
                    background: `${pcol}22`, borderRadius: 6, marginTop: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="font-['Cinzel']" style={{ color: pcol, fontSize: 24 }}>B</span>
                  </div>
                )}
                <p className="mt-1 font-['Cinzel'] text-center px-1 truncate w-full"
                  style={{ color: '#FFE9A0', fontSize: 9 }}>{e.name}</p>
                <p className="font-['Outfit'] uppercase tracking-[0.14em] mb-1"
                  style={{ color: pcol, fontSize: 7 }}>{PILLARS[e.pillar]?.name || e.pillar}</p>
                <span className="absolute top-1 left-1 rounded-full px-1.5 font-['Cinzel']"
                  style={{ background: `${pcol}CC`, color: '#0a0a0a', fontSize: 8, lineHeight: 1.4 }}>B</span>
              </button>
            );
          })}
          {/* User forged (rose badge, long-press to delete) */}
          {(customs || []).map((c) => {
            const pcol = PILLARS[c.pillar]?.color || GOLD;
            let holdTimer = null;
            const startHold = () => {
              holdTimer = setTimeout(() => {
                if (window.confirm(`Delete "${c.name}"? It will be removed from all assigned days.`)) {
                  onDeleteCustom?.(c.id);
                }
              }, 650);
            };
            const cancelHold = () => { if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; } };
            return (
              <button key={`user-${c.id}`} type="button"
                onClick={() => onOpenLibrary?.(c.id)}
                onPointerDown={startHold}
                onPointerUp={cancelHold}
                onPointerLeave={cancelHold}
                onContextMenu={(e) => e.preventDefault()}
                data-testid={`power-lib-user-${c.id}`}
                className="relative flex flex-col items-center rounded-xl overflow-hidden active:scale-95 transition-transform select-none"
                style={{
                  aspectRatio: '1 / 1.15',
                  background: `linear-gradient(160deg, rgba(255,158,184,0.12), rgba(0,0,0,0.6))`,
                  border: `1px solid rgba(255,158,184,0.5)`,
                  boxShadow: `0 0 8px rgba(255,158,184,0.2)`,
                  touchAction: 'manipulation',
                }}>
                {c.images?.[0] ? (
                  <img src={`${BACKEND_URL}${c.images[0]}`} alt={c.name}
                    style={{ width: '82%', height: '58%', objectFit: 'contain', marginTop: 4,
                      filter: `${pillarFilter(c.pillar)} drop-shadow(0 0 5px ${pcol}66)` }}/>
                ) : (
                  <div style={{ width: '82%', height: '58%',
                    background: 'rgba(255,158,184,0.15)', borderRadius: 6, marginTop: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="font-['Cinzel']" style={{ color: '#FF9EB8', fontSize: 24 }}>U</span>
                  </div>
                )}
                <p className="mt-1 font-['Cinzel'] text-center px-1 truncate w-full"
                  style={{ color: '#FFE9A0', fontSize: 9 }}>{c.name}</p>
                <p className="font-['Outfit'] uppercase tracking-[0.14em] mb-1"
                  style={{ color: '#FF9EB8', fontSize: 7 }}>User</p>
                <span className="absolute top-1 left-1 rounded-full px-1.5 font-['Cinzel']"
                  style={{ background: 'rgba(255,158,184,0.9)', color: '#0a0a0a', fontSize: 8, lineHeight: 1.4 }}>U</span>
              </button>
            );
          })}
        </div>
        {EXERCISES.length + (customs?.length || 0) === 0 && (
          <p className="text-center font-['Cormorant_Garamond'] italic pt-2"
            style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            No exercises yet.
          </p>
        )}
      </div>
      </>
      )}
    </div>
  );
};

/* ────────────── CUSTOM EXERCISE FORM ────────────── */
const CustomExerciseForm = ({ onBack, onCreated, defaultPillar = 'Lion', plan = null }) => {
  const [name, setName] = useState('');
  const [tip, setTip] = useState('');
  const [focus, setFocus] = useState('Custom');
  const [seconds, setSeconds] = useState(30);
  const [target, setTarget] = useState(15);
  const [unit, setUnit] = useState('reps');
  const [pillar, setPillar] = useState(defaultPillar);
  const [files, setFiles] = useState([null]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Day assignment (only shown when creating from Calendar context)
  const trainingDays = useMemo(
    () => (plan?.days || []).filter((d) => !d.rest).map((d) => d.index),
    [plan],
  );
  const [assignMode, setAssignMode] = useState('all'); // 'all' | 'selected' | 'none'
  const [selectedDays, setSelectedDays] = useState([]);
  const toggleDay = (i) => setSelectedDays((s) => s.includes(i) ? s.filter(x => x!==i) : [...s, i].sort((a,b)=>a-b));

  const submit = async () => {
    if (!name.trim()) { setErr('Name required'); return; }
    if (files.some((f) => !f)) { setErr('Please attach an image'); return; }
    setErr(''); setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('tip', tip.trim());
      fd.append('focus', focus.trim() || 'Custom');
      fd.append('seconds', String(seconds));
      fd.append('target', String(target));
      fd.append('unit', unit);
      fd.append('pillar', pillar);
      files.forEach((f, i) => fd.append(`image_${i}`, f));
      const { data } = await axios.post(`${BACKEND_URL}/api/power/custom-exercise`, fd);
      const targetDays = plan
        ? (assignMode === 'all' ? trainingDays
          : assignMode === 'selected' ? selectedDays
          : [])
        : [];
      onCreated(data, targetDays);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create custom exercise.');
    } finally { setSaving(false); }
  };

  const pill = (label, active, onClick, testid) => (
    <button key={label} onClick={onClick} data-testid={testid}
      className="rounded-lg px-3 py-1.5 font-['Cinzel'] uppercase tracking-[0.18em] text-[10px]"
      style={{
        background: active ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})` : 'rgba(255,255,255,0.03)',
        color: active ? '#0a0a0a' : 'rgba(255,255,255,0.7)',
        border: active ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
      }}>{label}</button>
  );

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5"
      style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)' }}
      data-testid="power-custom-form">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-1.5 rounded-full"
          data-testid="power-custom-back"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}40` }}>
          <ChevronLeft size={14} color={GOLD}/>
        </button>
        <p className="font-['Cinzel'] tracking-[0.14em]" style={{ color: '#FFE9A0', fontSize: 16 }}>
          Forge Your Own Exercise
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-3">
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Name</p>
          <input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="power-custom-name"
            placeholder="e.g. Panther Burpee"
            className="w-full rounded-lg px-3 py-2 font-['Cinzel'] outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}80`, color: '#FFE9A0', fontSize: 14 }}/>
        </div>

        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Focus (optional)</p>
          <input value={focus} onChange={(e) => setFocus(e.target.value)}
            data-testid="power-custom-focus"
            placeholder="e.g. Full body · Cardio"
            className="w-full rounded-lg px-3 py-2 font-['Outfit'] outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}40`, color: '#FFE9A0', fontSize: 12 }}/>
        </div>

        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Coaching Tip</p>
          <textarea value={tip} onChange={(e) => setTip(e.target.value)}
            data-testid="power-custom-tip"
            rows={2}
            placeholder="e.g. Explode up. Land soft. Keep core tight."
            className="w-full rounded-lg px-3 py-2 font-['Outfit'] outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}40`, color: '#FFE9A0', fontSize: 12 }}/>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Duration (s · 15–60)</p>
            <input type="number" min={15} max={60} step={1} value={seconds}
              onChange={(e) => {
                const v = Math.max(15, Math.min(60, +e.target.value || 15));
                setSeconds(v);
              }}
              data-testid="power-custom-seconds"
              className="w-full rounded-lg px-3 py-2 font-['Cinzel'] outline-none text-center"
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}40`, color: '#FFE9A0', fontSize: 14 }}/>
          </div>
          <div>
            <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Target</p>
            <input type="number" value={target} onChange={(e) => setTarget(+e.target.value)}
              data-testid="power-custom-target"
              className="w-full rounded-lg px-3 py-2 font-['Cinzel'] outline-none text-center"
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}40`, color: '#FFE9A0', fontSize: 14 }}/>
          </div>
          <div>
            <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1" style={{ color: GOLD, fontSize: 10 }}>Unit</p>
            <div className="flex gap-1">
              {pill('Reps', unit === 'reps', () => setUnit('reps'), 'power-custom-unit-reps')}
              {pill('Sec',  unit === 'seconds', () => setUnit('seconds'), 'power-custom-unit-seconds')}
            </div>
          </div>
        </div>

        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1.5" style={{ color: GOLD, fontSize: 10 }}>Pillar Colour</p>
          <div className="grid grid-cols-4 gap-1.5">
            {Object.entries(PILLARS).map(([k, p]) => (
              <button key={k} onClick={() => setPillar(k)}
                data-testid={`power-custom-pillar-${k.toLowerCase()}`}
                className="rounded-lg py-1.5 font-['Cinzel'] uppercase tracking-[0.16em] text-[9px]"
                style={{
                  background: pillar === k ? `linear-gradient(180deg, ${p.color}, ${p.color}88)` : 'rgba(255,255,255,0.03)',
                  color: pillar === k ? '#0a0a0a' : 'rgba(255,255,255,0.65)',
                  border: pillar === k ? `1px solid ${p.color}` : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: pillar === k ? `0 0 10px ${p.color}55` : 'none',
                }}>{p.label}</button>
            ))}
          </div>
        </div>

        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1.5" style={{ color: GOLD, fontSize: 10 }}>
            Attach One Image
          </p>
          <div className="grid grid-cols-1 gap-2">
            {[0].map((i) => (
              <label key={i}
                htmlFor={`power-custom-img-${i}`}
                className="rounded-xl aspect-square flex items-center justify-center cursor-pointer overflow-hidden mx-auto"
                style={{
                  background: '#050308',
                  border: files[i] ? `1px solid ${GOLD}` : `1px dashed ${GOLD}40`,
                  boxShadow: files[i] ? `0 0 10px ${GOLD}44` : 'none',
                  maxWidth: 200,
                  width: '100%',
                }}>
                {files[i] ? (
                  <img src={URL.createObjectURL(files[i])} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
                ) : (
                  <span className="font-['Outfit'] uppercase tracking-[0.2em]"
                    style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                    Tap to Attach
                  </span>
                )}
                <input id={`power-custom-img-${i}`} type="file" accept="image/*"
                  data-testid={`power-custom-img-${i}`}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    setFiles((arr) => arr.map((x, k) => (k === i ? f : x)));
                  }}/>
              </label>
            ))}
          </div>
        </div>

        {err && <p className="text-[11px] font-['Outfit'] text-center" style={{ color: '#e88a7a' }}>{err}</p>}

        {/* Assign-to-days (Calendar context only) */}
        {plan && (
          <div>
            <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1.5" style={{ color: GOLD, fontSize: 10 }}>
              Assign To Days
            </p>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {[
                { v: 'all',      label: 'All Training' },
                { v: 'selected', label: 'Pick Days' },
                { v: 'none',     label: 'Library Only' },
              ].map((o) => (
                <button key={o.v}
                  onClick={() => setAssignMode(o.v)}
                  data-testid={`power-custom-assign-${o.v}`}
                  className="rounded-lg py-1.5 font-['Cinzel'] uppercase tracking-[0.14em] text-[9px]"
                  style={{
                    background: assignMode === o.v ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})` : 'rgba(255,255,255,0.03)',
                    color: assignMode === o.v ? '#0a0a0a' : 'rgba(255,255,255,0.65)',
                    border: assignMode === o.v ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
                  }}>
                  {o.label}
                </button>
              ))}
            </div>
            {assignMode === 'selected' && (
              <div className="grid grid-cols-7 gap-1"
                style={{ background: 'rgba(255,255,255,0.02)', padding: 6, borderRadius: 10 }}>
                {plan.days.map((d) => (
                  <button key={d.index}
                    onClick={() => !d.rest && toggleDay(d.index)}
                    disabled={d.rest}
                    data-testid={`power-custom-assign-day-${d.index}`}
                    className="aspect-square rounded-md text-[9px] font-['Cinzel']"
                    style={{
                      background: d.rest ? 'rgba(255,255,255,0.02)'
                        : selectedDays.includes(d.index)
                          ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`
                          : 'rgba(255,255,255,0.04)',
                      color: d.rest ? 'rgba(255,255,255,0.25)'
                        : selectedDays.includes(d.index) ? '#0a0a0a' : 'rgba(255,255,255,0.6)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      cursor: d.rest ? 'not-allowed' : 'pointer',
                    }}>
                    {d.rest ? '·' : d.index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={submit} disabled={saving}
          data-testid="power-custom-save"
          className="w-full rounded-xl py-3 mt-2 font-['Cinzel'] uppercase tracking-[0.3em]"
          style={{
            background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`, color: '#0a0a0a', fontSize: 12,
            boxShadow: '0 0 18px rgba(197,160,89,0.4)', opacity: saving ? 0.6 : 1,
          }}>
          {saving ? 'Forging…' : 'Forge & Add'}
        </button>
      </div>
    </div>
  );
};

/* ────────────── DAY DETAIL (exercise list + reorder) ────────────── */
const DayDetail = ({ plan, dayIndex, resolveEx, onBack, onOpenTimer, onCompleteDay, onOpenCustom }) => {
  const day = plan.days[dayIndex];
  const [items, setItems] = useState(day.exercises);
  const [dragIdx, setDragIdx] = useState(null);
  const prog = progressionFor(dayIndex);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(day.exercises); }, [dayIndex, day.exercises]);

  const move = async (from, to) => {
    if (from === to || to < 0 || to >= items.length) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    try {
      await axios.post(`${BACKEND_URL}/api/power/day/${dayIndex}/reorder`, { exercises: arr });
    } catch { /* silent, UI already updated */ }
  };

  const powered = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/power/day/${dayIndex}/complete`);
      pingAuraFuse('power');
      onCompleteDay();
    } finally { setSaving(false); }
  };

  if (day.rest) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6" data-testid="power-day-rest">
        <button onClick={onBack} className="absolute top-16 left-4 p-1.5 rounded-full"
          data-testid="power-day-back"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}40` }}>
          <ChevronLeft size={14} color={GOLD}/>
        </button>
        <Sparkles size={40} color={GOLD}/>
        <p className="mt-3 font-['Cinzel'] tracking-[0.2em]" style={{ color: '#FFE9A0', fontSize: 20 }}>REST DAY</p>
        <p className="mt-2 font-['Outfit'] text-center max-w-xs"
          style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          Day {dayIndex + 1} — recovery is the forge of strength. Sleep. Hydrate. Return tomorrow.
        </p>
        <button onClick={powered}
          data-testid="power-day-powered-btn"
          className="mt-6 rounded-xl px-8 py-2.5 font-['Cinzel'] uppercase tracking-[0.3em]"
          style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`, color: '#0a0a0a', fontSize: 11, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Sealing…' : 'Mark Rested'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="power-day-detail">
      <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-shrink-0">
        <button onClick={onBack}
          data-testid="power-day-back"
          className="p-1.5 rounded-full"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}40` }}>
          <ChevronLeft size={14} color={GOLD}/>
        </button>
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: GOLD, fontSize: 9 }}>
            Day {dayIndex + 1} · {prog.weeks}
          </p>
          <p className="font-['Cinzel'] tracking-[0.14em]" style={{ color: '#FFE9A0', fontSize: 15 }}>
            {prog.focus}
          </p>
        </div>
        {/* Common Start Protocol button — runs exercises in sequence with Next between */}
        <button onClick={() => {
            if (!items.length) return;
            const firstId = items[0];
            const first = resolveEx(firstId);
            onOpenTimer(firstId, blissTarget(first, plan, dayIndex), 0);
          }}
          disabled={!items.length}
          data-testid="power-day-start-protocol-btn"
          className="ml-auto rounded-full px-3 py-1.5 font-['Cinzel'] uppercase tracking-[0.22em]"
          style={{
            background: `linear-gradient(135deg, #FF9EB8, ${GOLD_BRIGHT})`,
            color: '#0a0a0a', fontSize: 11,
            border: `1px solid ${GOLD_BRIGHT}`,
            boxShadow: `0 0 14px rgba(255,158,184,0.55)`,
            opacity: items.length ? 1 : 0.4,
          }}>
          ▶ Start
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        {items.map((exId, i) => {
          const ex = resolveEx(exId);
          if (!ex) return null;
          const pillar = PILLARS[ex.pillar] || PILLARS.Lion;
          const reps = blissTarget(ex, plan, dayIndex);
          const isSeconds = isHoldEx(ex);
          return (
            <div key={exId + i}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
              data-testid={`power-day-exercise-${exId}`}
              className="rounded-xl px-3 py-2.5 mb-2 flex items-center gap-3"
              style={{
                background: 'linear-gradient(90deg, rgba(20,15,10,0.85), rgba(10,7,5,0.6))',
                border: `1px solid ${pillar.color}55`,
                boxShadow: `inset 0 0 12px ${pillar.color}15`,
                cursor: 'grab',
              }}>
              <GripVertical size={14} color="rgba(255,255,255,0.35)"/>
              <div className="flex-shrink-0 rounded-md overflow-hidden"
                style={{ width: 56, height: 40, background: '#050308',
                  border: `1px solid ${pillar.color}30` }}>
                {ex.images?.[0] ? (
                  <img src={`${BACKEND_URL}${ex.images[0]}`} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain',
                      filter: `${pillarFilter(ex.pillar)} drop-shadow(0 0 4px ${pillar.color}66)` }}/>
                ) : (
                  <Silhouette pose={ex.poses?.[0]} size={56} stroke={pillar.color} glow={false}/>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-['Cinzel'] truncate" style={{ color: '#FFE9A0', fontSize: 13 }}>
                  {ex.name}{ex.isCustom ? ' ★' : ''}
                </p>
                <p className="font-['Outfit'] mt-0.5"
                  style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
                  {ex.focus} · <span style={{ color: pillar.color }}>{reps}{isSeconds ? 's' : ' reps'}</span>
                </p>
              </div>
              <button
                onClick={() => onOpenTimer(exId, reps, i)}
                data-testid={`power-start-${exId}`}
                className="rounded-full px-3 py-1.5 flex items-center gap-1"
                style={{
                  background: `linear-gradient(180deg, ${pillar.color}, ${GOLD_SOFT})`,
                  color: '#0a0a0a', fontSize: 10,
                }}>
                <Play size={10} fill="#0a0a0a"/>
                <span className="font-['Cinzel'] uppercase tracking-[0.2em]">Start</span>
              </button>
            </div>
          );
        })}

        {/* + Custom */}
        <button onClick={onOpenCustom}
          data-testid="power-add-custom-btn"
          className="w-full rounded-xl py-2.5 mt-1 mb-2 font-['Cinzel'] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
          style={{
            background: 'rgba(197,160,89,0.06)',
            border: `1px dashed ${GOLD}66`,
            color: GOLD, fontSize: 11,
          }}>
          + Add Custom Exercise
        </button>
      </div>

      {/* Powered button */}
      <div className="flex-shrink-0 px-4 pt-2"
        style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 32px)' }}>
        <button onClick={powered}
          disabled={saving || day.done}
          data-testid="power-day-powered-btn"
          className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
          style={{
            background: day.done
              ? 'rgba(197,160,89,0.15)'
              : `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
            color: day.done ? GOLD : '#0a0a0a',
            fontSize: 12,
            border: day.done ? `1px solid ${GOLD}55` : `1px solid ${GOLD}`,
            boxShadow: day.done ? 'none' : '0 0 18px rgba(197,160,89,0.35)',
            opacity: saving ? 0.6 : 1,
          }}>
          {day.done ? '✓ Sealed' : saving ? 'Sealing…' : 'Powered · Seal the Day'}
        </button>
      </div>
    </div>
  );
};

/* ────────────── USER-DAY DETAIL (localStorage-backed) ── */
/* ────────────── USER-DAY DETAIL (localStorage-backed) ── */
/* Mirrors Bliss DayDetail: header + master Start Protocol + per-row Start +
   Next-Exercise sequencing. "+ Add Exercise" opens a picker that includes
   BOTH Bliss defaults AND user-forged customs. */
const UserDay = ({ dayIdx, plan, customs, resolveEx, onBack, onOpenTimer }) => {
  const [cal, setCal] = useState(readUserCal());
  const [showPicker, setShowPicker] = useState(false);
  const day = cal[dayIdx] || { exercises: [], done: false };
  const setDay = (patch) => {
    const next = { ...cal, [dayIdx]: { ...day, ...patch } };
    setCal(next); writeUserCal(next);
  };
  const addExercise = (id) => {
    if (day.exercises.includes(id)) return;
    setDay({ exercises: [...day.exercises, id] });
  };
  const removeExercise = (id) => {
    setDay({ exercises: day.exercises.filter((x) => x !== id) });
  };
  const clearDay = () => setDay({ exercises: [], done: false });
  const sealDay = () => { setDay({ done: true }); pingAuraFuse('power'); };

  // Reps helper — Bliss uses normal-progression, customs use target
  const repsFor = (ex) => blissTarget(ex, plan, dayIdx);

  const isSeconds = (ex) => ex?.isCustom
    ? ex?.unit === 'seconds'
    : (ex?.id === 'stallion_plank' || ex?.id === 'eagle_cobra' || ex?.id === 'leopard_dash');

  // ── PICKER ── shows Bliss defaults + customs, tap to add
  if (showPicker) {
    // Build unified list
    const blissRows = EXERCISES.map((e) => ({ ...e, _kind: 'bliss' }));
    const customRows = customs.map((c) => ({
      id: c.id, name: c.name, pillar: c.pillar, focus: c.focus,
      images: c.images, target: c.target, seconds: c.seconds, unit: c.unit,
      isCustom: true, _kind: 'custom',
    }));
    const all = [...blissRows, ...customRows];
    return (
      <div className="flex-1 flex flex-col min-h-0" data-testid="power-user-picker">
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-shrink-0">
          <button onClick={() => setShowPicker(false)} data-testid="power-user-picker-back"
            className="p-1.5 rounded-full"
            style={{ background: 'rgba(255,158,184,0.08)', border: `1px solid rgba(255,158,184,0.4)` }}>
            <ChevronLeft size={14} color="#FF9EB8"/>
          </button>
          <div>
            <p className="font-['Outfit'] uppercase tracking-[0.28em]"
              style={{ color: '#FF9EB8', fontSize: 9 }}>Add Exercise · User Day {dayIdx + 1}</p>
            <p className="font-['Cinzel'] tracking-[0.14em]" style={{ color: '#FFE9A0', fontSize: 15 }}>
              Bliss & Custom Library
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {all.map((ex) => {
            const on = day.exercises.includes(ex.id);
            const pcol = PILLARS[ex.pillar]?.color || GOLD;
            const isB = ex._kind === 'bliss';
            const reps = isB ? blissTarget(ex, plan, dayIdx) : ex.target;
            const secs = isSeconds(ex);
            return (
              <button key={ex.id}
                type="button"
                onClick={() => (on ? removeExercise(ex.id) : addExercise(ex.id))}
                data-testid={`power-user-picker-${ex.id}`}
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 mb-1.5 text-left"
                style={{
                  background: on
                    ? 'linear-gradient(90deg, rgba(255,158,184,0.20), rgba(255,158,184,0.05))'
                    : 'rgba(255,255,255,0.03)',
                  border: on ? `1px solid #FF9EB8` : '1px solid rgba(255,255,255,0.08)',
                }}>
                <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0"
                  style={{ background: `${pcol}22`, border: `1px solid ${pcol}55` }}>
                  {ex.images?.[0] ? (
                    <img src={`${BACKEND_URL}${ex.images[0]}`} alt=""
                      style={{ width: 30, height: 26, objectFit: 'contain',
                        filter: `${pillarFilter(ex.pillar)} drop-shadow(0 0 3px ${pcol}66)` }}/>
                  ) : (
                    <Silhouette pose={ex.poses?.[0]} size={30} stroke={pcol} glow={false}/>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-['Cinzel'] truncate" style={{ color: '#FFE9A0', fontSize: 12 }}>
                    {ex.name}{isB ? '' : ' ★'}
                  </p>
                  <p className="font-['Outfit'] uppercase tracking-[0.2em] truncate"
                    style={{ color: pcol, fontSize: 8 }}>
                    {isB ? 'BLISS' : 'CUSTOM'} · {reps}{secs ? 's' : ' reps'}
                  </p>
                </div>
                <span className="font-['Cinzel']" style={{ color: on ? '#0a0a0a' : '#FF9EB8', fontSize: 11,
                  background: on ? '#FF9EB8' : 'transparent', padding: '3px 8px', borderRadius: 8,
                  border: `1px solid #FF9EB8`, minWidth: 32, textAlign: 'center' }}>{on ? '✓' : '+'}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-shrink-0 px-4 pt-2"
          style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 32px)' }}>
          <button onClick={() => setShowPicker(false)}
            data-testid="power-user-picker-done"
            className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
            style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
              color: '#0a0a0a', fontSize: 12, border: `1px solid ${GOLD}`,
              boxShadow: '0 0 14px rgba(197,160,89,0.5)' }}>
            ✓ Done · {day.exercises.length} Selected
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN USER-DAY DETAIL (mirrors Bliss DayDetail) ──
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="power-user-day-detail">
      <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-shrink-0">
        <button onClick={onBack} data-testid="power-user-day-back"
          className="p-1.5 rounded-full"
          style={{ background: 'rgba(255,158,184,0.08)', border: `1px solid rgba(255,158,184,0.4)` }}>
          <ChevronLeft size={14} color="#FF9EB8"/>
        </button>
        <div className="min-w-0">
          <p className="font-['Outfit'] uppercase tracking-[0.28em]"
            style={{ color: '#FF9EB8', fontSize: 9 }}>User Day {dayIdx + 1}</p>
          <p className="font-['Cinzel'] tracking-[0.14em] truncate"
            style={{ color: '#FFE9A0', fontSize: 15 }}>
            My Protocol
          </p>
        </div>
        {/* Master Start Protocol — runs day sequence with Next between */}
        <button onClick={() => {
            if (!day.exercises.length) return;
            const firstId = day.exercises[0];
            const first = resolveEx(firstId);
            const reps = repsFor(first);
            onOpenTimer(firstId, reps, 0);
          }}
          disabled={!day.exercises.length}
          data-testid="power-user-day-start-protocol-btn"
          className="ml-auto rounded-full px-3 py-1.5 font-['Cinzel'] uppercase tracking-[0.22em]"
          style={{
            background: `linear-gradient(135deg, #FF9EB8, ${GOLD_BRIGHT})`,
            color: '#0a0a0a', fontSize: 11,
            border: `1px solid ${GOLD_BRIGHT}`,
            boxShadow: `0 0 14px rgba(255,158,184,0.55)`,
            opacity: day.exercises.length ? 1 : 0.4,
          }}>
          ▶ Start
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        {day.exercises.length === 0 && (
          <p className="text-center font-['Cormorant_Garamond'] italic pt-6"
            style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            No exercises yet. Tap <span style={{ color: '#FF9EB8' }}>+ Add Exercise</span> to build your day
            from Bliss & Custom library.
          </p>
        )}
        {day.exercises.map((exId, i) => {
          const ex = resolveEx(exId);
          if (!ex) return null;
          const pillar = PILLARS[ex.pillar] || PILLARS.Lion;
          const reps = repsFor(ex);
          const secs = isSeconds(ex);
          return (
            <div key={exId + i}
              data-testid={`power-user-exercise-${exId}`}
              className="rounded-xl px-3 py-2.5 mb-2 flex items-center gap-3"
              style={{
                background: 'linear-gradient(90deg, rgba(20,15,10,0.85), rgba(10,7,5,0.6))',
                border: `1px solid ${pillar.color}55`,
                boxShadow: `inset 0 0 12px ${pillar.color}15`,
              }}>
              <div className="flex-shrink-0 rounded-md overflow-hidden"
                style={{ width: 56, height: 40, background: '#050308',
                  border: `1px solid ${pillar.color}30` }}>
                {ex.images?.[0] ? (
                  <img src={`${BACKEND_URL}${ex.images[0]}`} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain',
                      filter: `${pillarFilter(ex.pillar)} drop-shadow(0 0 4px ${pillar.color}66)` }}/>
                ) : (
                  <Silhouette pose={ex.poses?.[0]} size={56} stroke={pillar.color} glow={false}/>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-['Cinzel'] truncate" style={{ color: '#FFE9A0', fontSize: 13 }}>
                  {ex.name}{ex.isCustom ? ' ★' : ''}
                </p>
                <p className="font-['Outfit'] mt-0.5"
                  style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
                  {ex.focus || (ex.isCustom ? 'Custom' : 'Bliss')} · <span style={{ color: pillar.color }}>{reps}{secs ? 's' : ' reps'}</span>
                </p>
              </div>
              <button
                onClick={() => onOpenTimer(exId, reps, i)}
                data-testid={`power-user-start-${exId}`}
                className="rounded-full px-3 py-1.5 flex items-center gap-1"
                style={{
                  background: `linear-gradient(180deg, ${pillar.color}, ${GOLD_SOFT})`,
                  color: '#0a0a0a', fontSize: 10,
                }}>
                <Play size={10} fill="#0a0a0a"/>
                <span className="font-['Cinzel'] uppercase tracking-[0.2em]">Start</span>
              </button>
              <button
                onClick={() => removeExercise(exId)}
                data-testid={`power-user-remove-${exId}`}
                className="rounded-full p-1.5 flex-shrink-0"
                style={{ background: 'rgba(232,90,90,0.10)', color: '#E85A5A',
                  border: '1px solid rgba(232,90,90,0.35)' }}>
                <span style={{ fontSize: 11, lineHeight: 1 }}>✕</span>
              </button>
            </div>
          );
        })}

        {/* + Add Exercise (dashed) — opens picker (Bliss + Custom) */}
        <button onClick={() => setShowPicker(true)}
          data-testid="power-user-add-exercise-btn"
          className="w-full rounded-xl py-2.5 mt-1 mb-2 font-['Cinzel'] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
          style={{
            background: 'rgba(255,158,184,0.06)',
            border: `1px dashed #FF9EB8AA`,
            color: '#FF9EB8', fontSize: 11,
          }}>
          + Add Exercise · Bliss & Custom
        </button>
      </div>

      {/* Bottom dock — Clear + Seal Day */}
      <div className="flex-shrink-0 px-4 pt-2 grid grid-cols-2 gap-2"
        style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 32px)' }}>
        <button onClick={clearDay} data-testid="power-user-day-clear"
          className="rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.22em]"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)',
            border: '1px solid rgba(255,255,255,0.1)', fontSize: 10 }}>
          Clear
        </button>
        <button onClick={sealDay} disabled={day.done || !day.exercises.length}
          data-testid="power-user-day-seal"
          className="rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.24em]"
          style={{
            background: day.done ? 'rgba(255,158,184,0.15)' : `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
            color: day.done ? '#FF9EB8' : '#0a0a0a',
            border: `1px solid ${day.done ? '#FF9EB8' : GOLD}`, fontSize: 10,
            opacity: (day.done || !day.exercises.length) ? 0.5 : 1,
            boxShadow: (day.done || !day.exercises.length) ? 'none' : `0 0 14px ${GOLD}66`,
          }}>
          {day.done ? '✓ Sealed' : 'Seal Day'}
        </button>
      </div>
    </div>
  );
};

/* ────────────── EXERCISE TIMER ────────────── */
const Timer = ({ ex, targetReps, plan, onBack, fuseMode = false, onFuse, onNext, hasNext, nextLabel }) => {
  const pillar = ex ? (PILLARS[ex.pillar] || PILLARS.Lion) : null;
  const [state, setState] = useState('idle'); // idle|countdown|running|done
  const [countdown, setCountdown] = useState(3);
  // Per-exercise editable seconds; resets when the exercise changes
  const [secondsOverride, setSecondsOverride] = useState(() => timerSecondsFor(ex, targetReps, plan));
  const [remain, setRemain] = useState(() => timerSecondsFor(ex, targetReps, plan));
  const [editingTime, setEditingTime] = useState(false);
  const tickRef = useRef(null);

  // Reset timer state whenever a new exercise loads (e.g. via Next Exercise)
  useEffect(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setState('idle');
    setCountdown(3);
    const fresh = timerSecondsFor(ex, targetReps, plan);
    setSecondsOverride(fresh);
    setRemain(fresh);
    setEditingTime(false);
  }, [ex?.id, targetReps]); // eslint-disable-line react-hooks/exhaustive-deps


  // Preload real-image frames so they display instantly
  useEffect(() => {
    if (!ex?.images?.length) return;
    ex.images.forEach((src) => { const i = new Image(); i.src = BACKEND_URL + src; });
  }, [ex]);

  const cdRef = useRef(null);
  const beginRef = useRef(null);
  const stopAll = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (cdRef.current) { clearInterval(cdRef.current); cdRef.current = null; }
    if (beginRef.current) { clearTimeout(beginRef.current); beginRef.current = null; }
  };

  /* Deadline-based ticker: number, tick sound and ring all update in the
     same callback, polled every 100 ms so seconds never drift or skip. */
  const beginRunning = () => {
    const total = secondsOverride;
    const endAt = performance.now() + total * 1000;
    let shown = total;
    setState('running');
    setRemain(total);
    tickRef.current = setInterval(() => {
      const left = Math.max(0, Math.ceil((endAt - performance.now()) / 1000));
      if (left === shown) return;
      shown = left;
      setRemain(left);
      if (left <= 0) {
        stopAll();
        setState('done');
        falconPhoenix();            // ── FALCON + PHOENIX on timer end
        speak('Powered');
      } else {
        tick();                     // ── TIK every second
      }
    }, 100);
  };

  const startCountdown = () => {
    stopAll();
    lionRoar();                    // ── LION on Start
    setState('countdown');
    setCountdown(3);
    speak('Three.');
    const t0 = performance.now();
    let n = 3;
    cdRef.current = setInterval(() => {
      const step = 3 - Math.floor((performance.now() - t0) / 1000);
      if (step >= n) return;
      n = step;
      if (n === 2) { setCountdown(2); speak('Two.'); }
      else if (n === 1) { setCountdown(1); speak('One.'); }
      else {
        setCountdown(0);
        speak('Begin.');
        clearInterval(cdRef.current); cdRef.current = null;
        beginRef.current = setTimeout(() => { beginRef.current = null; beginRunning(); }, 450);
      }
    }, 50);
  };

  const restart = () => {
    stopAll();
    lionRoar();                    // ── LION on Restart
    setState('idle');
    setRemain(secondsOverride);
  };

  const hardStop = () => {
    stopAll();
    falconPhoenix();               // ── FALCON + PHOENIX on Stop
    setState('idle');
    setRemain(secondsOverride);
    stopSpeech();
  };

  useEffect(() => () => { stopAll(); stopSpeech(); }, []);

  if (!ex) return null;
  const pct = 1 - remain / secondsOverride;
  const strokeCol = pillar.color;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden items-center px-3 pb-2"
      style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 32px)' }}
      data-testid="power-timer">
      {/* Header — back, title, LARGE time setter */}
      <div className="w-full flex items-center gap-2 pt-2 pb-1.5 flex-shrink-0">
        <button onClick={onBack}
          data-testid="power-timer-back"
          className="p-2 rounded-full flex-shrink-0"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}40` }}>
          <ChevronLeft size={16} color={GOLD}/>
        </button>
        <p className="font-['Cinzel'] tracking-[0.16em] uppercase flex-1 truncate"
          style={{ color: GOLD_BRIGHT, fontSize: 12, letterSpacing: '0.16em' }}>
          {ex.name}
        </p>
        {/* Per-exercise time editor (top-right) — enlarged, gold-forged pill */}
        {state === 'idle' ? (
          editingTime ? (
            <div className="flex items-center gap-1.5 rounded-full pl-2 pr-1 py-1 flex-shrink-0"
              data-testid="power-timer-time-edit"
              style={{ background: 'rgba(15,10,5,0.92)', border: `1.5px solid ${GOLD}`,
                       boxShadow: `0 0 14px ${GOLD}55, inset 0 0 8px ${GOLD}25` }}>
              <input type="number" min={5} max={300} value={secondsOverride}
                onChange={(e) => setSecondsOverride(Math.max(5, Math.min(300, +e.target.value || 5)))}
                onBlur={() => { setRemain(secondsOverride); setEditingTime(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { setRemain(secondsOverride); setEditingTime(false); } }}
                data-testid="power-timer-seconds-input"
                autoFocus
                className="w-14 rounded-md px-2 py-1 text-center font-['Cinzel'] outline-none"
                style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${GOLD}80`,
                         color: '#FFE9A0', fontSize: 18, fontWeight: 700 }}/>
              <span className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 13, paddingRight: 4 }}>s</span>
            </div>
          ) : (
            <button type="button" onClick={() => setEditingTime(true)}
              data-testid="power-timer-time-edit-btn"
              className="rounded-full px-4 py-2 flex items-center gap-1.5 font-['Cinzel'] uppercase tracking-[0.16em] flex-shrink-0"
              style={{ background: `linear-gradient(135deg, rgba(197,160,89,0.28), rgba(197,160,89,0.10))`,
                       color: GOLD_BRIGHT,
                       border: `1.5px solid ${GOLD}`,
                       fontSize: 16, fontWeight: 700,
                       boxShadow: `0 0 14px ${GOLD}55, inset 0 0 8px ${GOLD}25` }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{secondsOverride}s</span>
              <span style={{ fontSize: 12, opacity: 0.9 }}>✎</span>
            </button>
          )
        ) : (
          <span className="font-['Cinzel'] rounded-full px-3 py-1.5 flex-shrink-0"
            style={{ color: GOLD_BRIGHT, fontSize: 15, fontWeight: 700,
                     background: 'rgba(197,160,89,0.12)', border: `1px solid ${GOLD}55` }}>
            {secondsOverride}s
          </span>
        )}
      </div>

      {/* Silhouette panel — flex-1, self-contained, no overflow */}
      <div className={`w-full rounded-2xl px-2.5 py-2 flex-1 flex flex-col items-center justify-between min-h-0 relative overflow-hidden ${state === 'running' ? 'power-disco' : ''}`}
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.10) 0%, rgba(0,0,0,0) 60%), linear-gradient(180deg, rgba(15,10,7,0.9), rgba(5,3,8,0.95))',
          border: `1px solid ${strokeCol}55`,
          boxShadow: `0 0 24px ${strokeCol}30, inset 0 0 30px ${strokeCol}10`,
          '--disco-color': strokeCol,
        }}>
        {ex.video ? (
          <ExerciseVideo key={`${ex.id}-${secondsOverride}`} src={`${BACKEND_URL}${ex.video}`} playing={state === 'running'} stroke={strokeCol} pillar={ex.pillar}
            clipReps={ex.clipReps || 1}
            repSeconds={HOLD_IDS.has(ex.id) || !targetReps ? null : secondsOverride / targetReps}/>
        ) : (
        <>
        {/* 2×2 image grid — two equal rows, share vertical space, no overflow */}
        <div className="w-full grid grid-cols-2 grid-rows-2 gap-2 flex-1 min-h-0">
          {[0, 1, 2, 3].map((i) => {
            const src = ex.images?.[i] ? `${BACKEND_URL}${ex.images[i]}` : null;
            const pose = ex.poses?.[Math.min(i, ex.poses.length - 1)];
            return (
              <div key={i}
                className="rounded-xl overflow-hidden flex items-center justify-center min-h-0 min-w-0"
                style={{
                  background: '#050308',
                  border: `1px solid ${strokeCol}55`,
                  boxShadow: `inset 0 0 14px ${strokeCol}20, 0 0 10px ${strokeCol}25`,
                }}>
                {src ? (
                  <img src={src} alt=""
                    style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain',
                      filter: `${pillarFilter(ex.pillar)} drop-shadow(0 0 6px ${strokeCol}80)` }}/>
                ) : (
                  <Silhouette pose={pose} size={90} stroke={strokeCol}/>
                )}
              </div>
            );
          })}
        </div>

        </>
        )}

        {/* Countdown overlay (absolute so it doesn't push layout) */}
        {state === 'countdown' && (
          <div className="absolute inset-0 flex items-center justify-center font-['Cinzel'] pointer-events-none"
            style={{ color: GOLD_BRIGHT, fontSize: 72, textShadow: '0 0 24px rgba(197,160,89,0.7)',
                     background: 'rgba(0,0,0,0.35)' }}>
            {countdown === 0 ? 'BEGIN' : countdown}
          </div>
        )}

        {/* Timer ring + controls */}
        <div className="mt-2 flex items-center gap-3 flex-shrink-0">
          <div className="relative rounded-full" style={{ width: 68, height: 68, boxShadow: `0 0 10px ${GOLD}55` }}>
            <svg width="68" height="68" viewBox="0 0 40 40" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="20" cy="20" r="17" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" fill="none"/>
              <circle cx="20" cy="20" r="17" stroke={GOLD} strokeWidth="2.5" fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 17}`}
                strokeDashoffset={`${(1 - pct) * 2 * Math.PI * 17}`}
                style={{ transition: 'stroke-dashoffset 1s linear' }}/>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-['Cinzel']"
              style={{ color: '#FFE9A0', fontSize: 14 }}>
              {String(Math.floor(remain / 60)).padStart(2,'0')}:{String(remain % 60).padStart(2,'0')}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {state !== 'running' && (
              <button onClick={startCountdown}
                data-testid="power-timer-start"
                className="rounded-full px-4 py-2 flex items-center gap-1.5"
                style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`, color: '#0a0a0a' }}>
                <Play size={12} fill="#0a0a0a"/>
                <span className="font-['Cinzel'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Start</span>
              </button>
            )}
            {state === 'running' && (
              <button onClick={hardStop}
                data-testid="power-timer-stop"
                className="rounded-full px-4 py-2 flex items-center gap-1.5"
                style={{ background: 'rgba(232,90,90,0.15)', color: '#E85A5A', border: '1px solid rgba(232,90,90,0.4)' }}>
                <Square size={11} fill="#E85A5A"/>
                <span className="font-['Cinzel'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Stop</span>
              </button>
            )}
            <button onClick={restart}
              data-testid="power-timer-restart"
              className="rounded-full p-2"
              style={{ background: 'rgba(197,160,89,0.1)', color: GOLD, border: `1px solid ${GOLD}40` }}>
              <RotateCcw size={13}/>
            </button>
          </div>
        </div>

        {/* Info — Target + Tip, compact */}
        <div className="mt-2 w-full rounded-xl px-3 py-2 flex-shrink-0"
          style={{
            background: 'linear-gradient(180deg, rgba(20,15,10,0.9), rgba(5,3,8,0.95))',
            border: `1px solid ${strokeCol}66`,
            boxShadow: `inset 0 0 14px ${strokeCol}22, 0 0 12px ${strokeCol}30`,
          }}>
          <div className="flex items-baseline justify-center gap-3 mb-0.5">
            <p className="font-['Outfit'] uppercase tracking-[0.30em]"
              style={{ color: strokeCol, fontSize: 9 }}>Target</p>
            <p className="font-['Cinzel'] tracking-[0.08em]"
              style={{ color: '#FFE9A0', fontSize: 18, textShadow: `0 0 12px ${strokeCol}66` }}>
              {targetReps}{(ex.isCustom ? ex.unit === 'seconds' : (ex.id === 'stallion_plank' || ex.id === 'eagle_cobra' || ex.id === 'leopard_dash')) ? ' Seconds' : ' Reps'}
            </p>
          </div>
          <p className="text-center font-['Outfit'] leading-snug line-clamp-2"
            style={{ color: strokeCol, fontSize: 11, letterSpacing: '0.02em' }}>
            {ex.tip}
          </p>
        </div>
      </div>

      {/* Tagline — very compact, always visible */}
      <p className="mt-1 font-['Outfit'] text-center flex-shrink-0"
        style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, lineHeight: 1 }}>
        Bliss Physical Protocol · Form is Empire
      </p>

      {/* Fuse — appears after a Library-launched exercise completes */}
      {fuseMode && state === 'done' && (
        <button onClick={() => onFuse?.(ex.id, ex.name)}
          data-testid="power-timer-fuse"
          className="mt-1.5 w-full rounded-xl py-2 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2 flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
            color: '#0a0a0a', fontSize: 12,
            boxShadow: '0 0 22px rgba(233,199,122,0.55), inset 0 1px 0 rgba(255,255,255,0.3)',
            border: `1px solid ${GOLD}`,
          }}>
          ✦ Fuse This Rep Into The Vessel
        </button>
      )}
      {fuseMode && state !== 'done' && (
        <p className="mt-1 font-['Cormorant_Garamond'] italic text-center flex-shrink-0"
          style={{ color: `${GOLD}88`, fontSize: 10 }}>
          Complete the timer to Fuse this Library rep.
        </p>
      )}

      {/* Next / Complete — sequence flow */}
      {onNext && (
        <button onClick={onNext}
          data-testid="power-timer-next-btn"
          className="mt-1.5 w-full rounded-xl py-2 font-['Cinzel'] uppercase tracking-[0.28em] flex-shrink-0"
          style={{
            background: hasNext
              ? `linear-gradient(135deg, #FF9EB8, ${GOLD_BRIGHT})`
              : `linear-gradient(135deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
            color: '#0a0a0a', fontSize: 12,
            border: `1px solid ${hasNext ? '#FF9EB8' : GOLD}`,
            boxShadow: hasNext
              ? '0 0 18px rgba(255,158,184,0.55)'
              : `0 0 18px ${GOLD}77`,
          }}>
          {nextLabel || (hasNext ? '▶ Next Exercise →' : '✓ Complete Day')}
        </button>
      )}
    </div>
  );
};

/* ────────────── ROOT MODAL ────────────── */
/* Looping exercise video — plays only while the timer runs, rests on frame 0 otherwise. */
const HOLD_IDS = new Set(['stallion_plank', 'eagle_cobra', 'leopard_dash']);
const ExerciseVideo = ({ src, playing, stroke, pillar, repSeconds = null, clipReps = 1 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (playing) { v.play().catch(() => {}); }
    else { v.pause(); v.currentTime = 0; }
  }, [playing]);
  return (
    <div className="w-full flex-1 min-h-0 rounded-xl overflow-hidden flex items-center justify-center"
      style={{ background: '#050308', border: `1px solid ${stroke}55`, boxShadow: `inset 0 0 14px ${stroke}20, 0 0 10px ${stroke}25` }}>
      <video ref={ref} muted loop playsInline preload="auto" data-testid="power-exercise-video"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (!(d > 0)) return;
          // Rep exercises: one video rep = one counted rep; holds: loop snaps to whole seconds.
          const target = repSeconds ? repSeconds * clipReps : Math.max(1, Math.round(d));
          e.currentTarget.playbackRate = Math.min(2.5, Math.max(0.4, d / target));
        }}
        style={{ width: '100%', height: '100%', objectFit: 'contain', filter: pillarFilter(pillar) }}>
        <source src={src.replace(/\.mp4$/, '.webm')} type="video/webm"/>
        <source src={src} type="video/mp4"/>
      </video>
    </div>
  );
};

export const ThePower = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [customs, setCustoms] = useState([]);
  const [screen, setScreen] = useState('calendar'); // setup | calendar | day | timer | new-custom
  const [dayIdx, setDayIdx] = useState(0);
  const [timerCtx, setTimerCtx] = useState(null); // { exerciseId, targetReps, fromLibrary }
  const [fuseToast, setFuseToast] = useState('');
  const [calendarTab, setCalendarTab] = useState('bliss'); // persists across screen changes

  // Back walks: sub-screen → calendar → close.
  useHistoryClose(true, useCallback(() => {
    if (screen !== 'calendar') {
      setScreen('calendar');
      setTimerCtx(null);
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) {}
    } else {
      onClose();
    }
  }, [screen, onClose]));

  const resolveEx = (id) => {
    if (EX_BY_ID[id]) return EX_BY_ID[id];
    const c = customs.find((c) => c.id === id);
    if (!c) return null;
    return {
      id: c.id, name: c.name, pillar: c.pillar, focus: c.focus,
      tip: c.tip || 'Custom exercise.',
      baseReps: { normal: c.target, active: c.target, athletic: c.target },
      seconds: c.seconds,
      images: c.images,
      poses: [],  // no SVG fallback
      unit: c.unit,
      isCustom: true,
    };
  };

  const refresh = async () => {
    try {
      const [planRes, customsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/power/plan`),
        axios.get(`${BACKEND_URL}/api/power/custom-exercises`).catch(() => ({ data: [] })),
      ]);
      setCustoms(customsRes.data || []);
      const data = planRes.data;
      if (!data.has_plan) { setPlan(null); setScreen('setup'); }
      else { setPlan(data); setScreen((s) => (s === 'setup' ? 'calendar' : s)); }
    } catch { setScreen('setup'); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleSaved = async (savedPlan) => { setPlan(savedPlan); setScreen('calendar'); };

  const handleReset = async () => {
    try { await axios.delete(`${BACKEND_URL}/api/power/plan`); } catch { /* noop */ }
    setPlan(null); setScreen('setup');
  };

  const handleDeleteCustom = async (cx_id) => {
    try { await axios.delete(`${BACKEND_URL}/api/power/custom-exercise/${cx_id}`); } catch { /* noop */ }
    await refresh();
  };

  const openLibraryTimer = (exerciseId) => {
    const ex = EX_BY_ID[exerciseId] || customs.find(c => c.id === exerciseId);
    if (!ex) return;
    // Use progression targeting for Bliss; use `target` for user-forged
    setTimerCtx({ exerciseId, targetReps: blissTarget(ex, plan, dayIdx), fromLibrary: true });
    setScreen('timer');
  };

  const recordFuse = (exId, exName) => {
    try {
      const key = 'bliss_power_fused_reps';
      const arr = JSON.parse(localStorage.getItem(key) || '[]');
      arr.push({ id: exId, name: exName, at: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(arr));
    } catch { /* noop */ }
    pingAuraFuse('power');
    setFuseToast(`✦ Fused — ${exName}`);
    setTimeout(() => setFuseToast(''), 2400);
    setScreen('calendar');
    setTimerCtx(null);
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col" style={{ background: BG }} data-testid="power-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#FF6478" testId="power-close-btn"/>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: '0 0 10px rgba(197,160,89,0.35)' }}>
          POWER
        </p>
        <span className="w-14"/>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="font-['Outfit'] uppercase tracking-[0.35em]" style={{ color: GOLD, fontSize: 10 }}>
            Consulting the Protocol…
          </p>
        </div>
      ) : screen === 'setup' ? (
        <Setup initial={plan} onSaved={handleSaved}/>
      ) : screen === 'new-custom' ? (
        <CustomExerciseForm
          plan={plan}
          onBack={() => setScreen('calendar')}
          onCreated={async (cx, targetDays) => {
            // Append cx.id to each selected day
            const days = targetDays && targetDays.length ? targetDays : [];
            for (const dIdx of days) {
              try {
                const existing = plan.days[dIdx]?.exercises || [];
                await axios.post(`${BACKEND_URL}/api/power/day/${dIdx}/reorder`,
                  { exercises: [...existing, cx.id] });
              } catch { /* noop */ }
            }
            await refresh();
            setScreen('calendar');
          }}
        />
      ) : screen === 'timer' && timerCtx ? (
        <Timer ex={resolveEx(timerCtx.exerciseId)} targetReps={timerCtx.targetReps} plan={plan}
          fuseMode={!!timerCtx.fromLibrary}
          onFuse={recordFuse}
          onNext={typeof timerCtx.seqIdx === 'number' ? () => {
            // Choose sequence source: user-day (localStorage) vs bliss-day (plan)
            const isUser = timerCtx.source === 'user';
            const items = isUser
              ? (readUserCal()[dayIdx]?.exercises || [])
              : (plan?.days[dayIdx]?.exercises || []);
            const nextIdx = timerCtx.seqIdx + 1;
            if (nextIdx >= items.length) {
              setTimerCtx(null); setScreen(isUser ? 'user-day' : 'day'); return;
            }
            const nextId = items[nextIdx];
            const nextEx = resolveEx(nextId);
            const nextReps = blissTarget(nextEx, plan, dayIdx);
            setTimerCtx({ exerciseId: nextId, targetReps: nextReps, fromLibrary: false,
              seqIdx: nextIdx, source: timerCtx.source });
          } : undefined}
          hasNext={typeof timerCtx.seqIdx === 'number'
            ? (timerCtx.source === 'user'
                ? (timerCtx.seqIdx + 1) < (readUserCal()[dayIdx]?.exercises?.length || 0)
                : (timerCtx.seqIdx + 1) < (plan?.days[dayIdx]?.exercises?.length || 0))
            : false}
          onBack={() => setScreen(timerCtx.fromLibrary ? 'calendar'
            : (timerCtx.source === 'user' ? 'user-day' : 'day'))}/>
      ) : screen === 'user-day' ? (
        <UserDay dayIdx={dayIdx} plan={plan} customs={customs} resolveEx={resolveEx}
          onBack={() => setScreen('calendar')}
          onOpenTimer={(exerciseId, targetReps, seqIdx) => {
            setTimerCtx({ exerciseId, targetReps, fromLibrary: false, seqIdx, source: 'user' });
            setScreen('timer');
          }}/>
      ) : screen === 'day' && plan ? (
        <DayDetail plan={plan} dayIndex={dayIdx} resolveEx={resolveEx}
          onBack={() => setScreen('calendar')}
          onOpenCustom={() => setScreen('new-custom')}
          onOpenTimer={(exerciseId, targetReps, seqIdx) => {
            setTimerCtx({ exerciseId, targetReps, fromLibrary: false, seqIdx, source: 'bliss' });
            setScreen('timer');
          }}
          onCompleteDay={async () => { await refresh(); setScreen('calendar'); }}/>
      ) : plan ? (
        <Calendar plan={plan} customs={customs}
          tab={calendarTab} setTab={setCalendarTab}
          onOpenDay={(i) => { setCalendarTab('bliss'); setDayIdx(i); setScreen('day'); }}
          onOpenUserDay={(i) => { setCalendarTab('user'); setDayIdx(i); setScreen('user-day'); }}
          onOpenLibrary={openLibraryTimer}
          onReset={handleReset}
          onOpenCustom={() => setScreen('new-custom')}
          onDeleteCustom={handleDeleteCustom}/>
      ) : null}

      {/* Fuse Toast */}
      {fuseToast && (
        <div className="fixed left-1/2 -translate-x-1/2 rounded-full px-4 py-2 z-[240]"
          data-testid="power-fuse-toast"
          style={{
            bottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 30px)',
            background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
            color: '#0a0a0a', fontSize: 11,
            boxShadow: '0 0 22px rgba(197,160,89,0.55)',
            border: `1px solid ${GOLD_BRIGHT}`,
            fontFamily: 'Cinzel', letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
          {fuseToast}
        </div>
      )}
    </div>
  );
};

/* Power tile icon — laurel & bolt */
export const PowerTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(197,160,89,0.6))' }}>
    <defs>
      <linearGradient id="pwrIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F0DAA3"/>
        <stop offset="0.6" stopColor="#C5A059"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
    </defs>
    <path d="M4 12 C 4 6, 8 3, 12 3 C 16 3, 20 6, 20 12 C 20 14, 19 15, 18 15 L 6 15 C 5 15, 4 14, 4 12 Z"
      fill="none" stroke="url(#pwrIcon)" strokeWidth="1.4"/>
    <path d="M13 7 L 9 13 L 12 13 L 10 18 L 15 12 L 12 12 L 14 7 Z"
      fill="url(#pwrIcon)" stroke="#0a0a0a" strokeWidth="0.4"/>
  </svg>
);

export default ThePower;
